import { Button } from '@/components/ui/button';
import { useContentDetail } from '@/hooks/useContentQueries';
import { OBSERVATION_CAPTURE_LABELS } from '@/components/widget/observation-capture/labels';
import { ChevronUp, ExternalLink, Link2, MoreHorizontal, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { resolveLinkSeed } from './provenance-link-seed';

export interface ProvenancePhotoListItem {
  id: string;
  remoteId?: number;
  name: string;
  url: string;
}

export interface ProvenancePhotoListProps {
  photos: ProvenancePhotoListItem[];
  onRemove?: (index: number) => void;
  onAddUnlinked?: () => void;
  onAddLinked?: (linkContentId?: number) => void;
  onOpenContent?: (contentId: number) => void;
}

export function ProvenancePhotoList({
  photos,
  onRemove,
  onAddUnlinked,
  onAddLinked,
  onOpenContent,
}: ProvenancePhotoListProps) {
  if (photos.length === 0) return null;

  const linkSeed = resolveLinkSeed(photos);

  return (
    <div className="py-2 text-xs bg-muted/30 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium">Photos</p>
        <div className="flex items-center gap-2">
          {onAddUnlinked ? (
            <Button variant="outline" size="sm" onClick={onAddUnlinked}>
              <Plus className="h-3 w-3 mr-1" />
              {OBSERVATION_CAPTURE_LABELS.addUnlinked}
            </Button>
          ) : null}
          {onAddLinked && linkSeed !== undefined ? (
            <Button variant="outline" size="sm" onClick={() => onAddLinked(linkSeed)}>
              <Link2 className="h-3 w-3 mr-1" />
              {OBSERVATION_CAPTURE_LABELS.addLinked}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="space-y-2">
        {photos.map((photo, index) => (
          <PhotoListItem
            key={photo.id}
            photo={photo}
            index={index}
            onRemove={onRemove}
            onOpenContent={onOpenContent}
          />
        ))}
      </div>
    </div>
  );
}

function PhotoListItem({
  photo,
  index,
  onRemove,
  onOpenContent,
}: {
  photo: ProvenancePhotoListItem;
  index: number;
  onRemove?: (index: number) => void;
  onOpenContent?: (contentId: number) => void;
}) {
  const detailQuery = useContentDetail(photo.remoteId ?? 0);
  const detail = detailQuery.data;
  const remoteId = photo.remoteId;
  const [expanded, setExpanded] = useState(false);
  const title =
    typeof detail?.title === 'string' && detail.title.trim().length > 0
      ? detail.title.trim()
      : photo.name;
  const description =
    typeof detail?.description === 'string' && detail.description.trim().length > 0
      ? detail.description.trim()
      : null;
  const imageUrl =
    photo.url ||
    (detail?.contentType === 'PHOTO' && typeof detail.uri === 'string' && detail.uri.trim().length > 0
      ? `/api/files/photo/${detail.uri}`
      : '');

  return (
    <div className="rounded border p-2 space-y-2">
      <div className="flex items-center justify-between gap-2">
        {imageUrl ? (
          <img src={imageUrl} alt={title} className="h-16 w-16 rounded object-cover" />
        ) : (
          <div className="h-16 w-16 rounded bg-muted shrink-0" />
        )}
        <div className="flex items-center gap-1">
          {typeof remoteId === 'number' && onOpenContent ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              aria-label="Open photo"
              title="Open photo"
              onClick={() => onOpenContent(remoteId)}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          {onRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => onRemove(index)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      </div>
      <p className="text-xs font-medium">{title}</p>
      {description ? (
        <div className="flex items-start gap-1">
          <p className={`text-xs text-muted-foreground flex-1 ${expanded ? 'whitespace-pre-wrap' : 'truncate'}`}>
            {description}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={() => setExpanded((previous) => !previous)}
            title={expanded ? 'Show less' : 'Show more'}
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <MoreHorizontal className="h-3.5 w-3.5" />}
          </Button>
        </div>
      ) : photo.remoteId ? (
        <p className="text-xs text-muted-foreground">Summary processing...</p>
      ) : null}
    </div>
  );
}
